import React, { createContext, useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import empresaService from '../services/empresaService';
import { migrateTokensToSanctum, validateSanctumToken } from '../utils/tokenMigration';
import { clearAllCompanyStorage, prepareCompanyChange, clearForceCompanySelector, isForcingCompanySelector } from '../utils/companyStorage';
import {
  getEmpresaIdFromUrl,
  pathWithEmpresa,
  clearEmpresaFromUrl,
} from '../utils/companyUrl';
import { usuarioSoloUnaEmpresa, empresaIdAsignada } from '../utils/usuarioEmpresa';

const AuthContext = createContext(null);

async function fetchCompanyById(empresaId) {
  const response = await empresaService.obtenerEmpresa(empresaId);
  if (response?.status === 'success' && response?.data?.id) {
    return response.data;
  }
  return null;
}

export const AuthProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isResolvingCompany, setIsResolvingCompany] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [forceCompanySelector, setForceCompanySelector] = useState(() => isForcingCompanySelector());

  const empresaIdFromUrl = useMemo(
    () => getEmpresaIdFromUrl(`${window.location.origin}${location.pathname}${location.search}`),
    [location.pathname, location.search]
  );

  const resolveCompanyFromUrl = useCallback(async (empresaId) => {
    if (!empresaId) {
      setSelectedCompany(null);
      return null;
    }

    setIsResolvingCompany(true);
    try {
      const company = await fetchCompanyById(empresaId);
      if (company) {
        authService.setSelectedCompany(company);
        setSelectedCompany(company);
        return company;
      }

      clearEmpresaFromUrl({ pathname: location.pathname });
      setSelectedCompany(null);
      return null;
    } finally {
      setIsResolvingCompany(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    const initializeAuth = async () => {
      setIsLoading(true);

      try {
        const hadOldTokens = migrateTokensToSanctum();

        if (hadOldTokens) {
          setUser(null);
          setSelectedCompany(null);
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }

        const currentToken = authService.getToken();
        if (currentToken && !validateSanctumToken(currentToken)) {
          authService.clearStorage();
          setUser(null);
          setSelectedCompany(null);
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }

        const storedUser = authService.getUser();
        const isAuth = authService.isAuthenticated();

        if (isAuth && storedUser) {
          const userResult = await authService.getCurrentUser();

          if (userResult.success) {
            setUser(userResult.user);
            setIsAuthenticated(true);
          } else {
            authService.clearStorage();
            setUser(null);
            setSelectedCompany(null);
            setIsAuthenticated(false);
          }
        } else {
          setUser(null);
          setSelectedCompany(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Error al inicializar autenticación:', error);
        authService.clearStorage();
        setUser(null);
        setSelectedCompany(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !user?.empresa_id) {
      return;
    }

    if (forceCompanySelector) {
      clearForceCompanySelector();
      setForceCompanySelector(false);
    }

    if (Number(selectedCompany?.id) === Number(user.empresa_id)) {
      if (Number(empresaIdFromUrl) !== Number(user.empresa_id)) {
        navigate(pathWithEmpresa(`${location.pathname}${location.search}`, user.empresa_id), { replace: true });
      }
      return;
    }

    resolveCompanyFromUrl(user.empresa_id).then((company) => {
      if (company) {
        navigate(pathWithEmpresa('/', company.id), { replace: true });
      }
    });
  }, [isAuthenticated, user?.empresa_id, selectedCompany?.id, forceCompanySelector, resolveCompanyFromUrl, empresaIdFromUrl, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!isAuthenticated) {
      setSelectedCompany(null);
      return;
    }

    if (usuarioSoloUnaEmpresa(user)) {
      return;
    }

    if (isForcingCompanySelector() || forceCompanySelector) {
      return;
    }

    if (!empresaIdFromUrl) {
      return;
    }

    if (Number(selectedCompany?.id) === Number(empresaIdFromUrl)) {
      return;
    }

    resolveCompanyFromUrl(empresaIdFromUrl);
  }, [isAuthenticated, empresaIdFromUrl, selectedCompany?.id, forceCompanySelector, resolveCompanyFromUrl, user]);

  // Si la ruta perdió ?empresa= pero aún hay empresa en contexto, restaurar el parámetro.
  useEffect(() => {
    if (!isAuthenticated || empresaIdFromUrl || !selectedCompany?.id || isForcingCompanySelector() || forceCompanySelector) {
      return;
    }

    if (location.pathname === '/' && !location.search.includes('empresa=')) {
      return;
    }

    const destino = pathWithEmpresa(`${location.pathname}${location.search}`, selectedCompany.id);
    navigate(destino, { replace: true });
  }, [
    isAuthenticated,
    empresaIdFromUrl,
    selectedCompany?.id,
    location.pathname,
    location.search,
    navigate,
    forceCompanySelector,
  ]);

  const login = useCallback(async (email, password) => {
    // No usar isLoading global aquí: el Login tiene su propio spinner.
    // Si se pone isLoading=true con user aún null, App desmonta Login y
    // muestra "Verificando autenticación...", y un reload posterior
    // hace aparecer el selector dos veces.
    try {
      const result = await authService.login(email, password);

      if (result.success) {
        setUser(result.user);
        setIsAuthenticated(true);
        setForceCompanySelector(false);
        clearForceCompanySelector();

        const empresaId = empresaIdAsignada(result.user);
        if (empresaId) {
          const company = await fetchCompanyById(empresaId);
          if (company) {
            authService.setSelectedCompany(company);
            setSelectedCompany(company);
            navigate(pathWithEmpresa('/', company.id), { replace: true });
            return result;
          }
        }

        setSelectedCompany(null);
        clearEmpresaFromUrl({ pathname: '/' });
        navigate('/', { replace: true });
      } else {
        setUser(null);
        setIsAuthenticated(false);
        setSelectedCompany(null);
      }

      return result;
    } catch (error) {
      console.error('Error en login:', error);
      setUser(null);
      setIsAuthenticated(false);
      setSelectedCompany(null);
      return {
        success: false,
        message: 'Error de conexión',
      };
    }
  }, [navigate]);

  const register = useCallback(async (userData) => {
    setIsLoading(true);

    try {
      const result = await authService.register(userData);

      if (result.success) {
        setUser(result.user);
        setIsAuthenticated(true);
        setSelectedCompany(null);
        clearEmpresaFromUrl({ pathname: '/' });
        navigate('/', { replace: true });
      }

      return result;
    } catch (error) {
      console.error('Error en registro:', error);
      return {
        success: false,
        message: 'Error de conexión',
      };
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  const logout = useCallback(async () => {
    setIsLoading(true);

    try {
      await authService.logout();
    } catch (error) {
      console.error('Error en logout:', error);
    } finally {
      clearAllCompanyStorage(user?.id);
      setUser(null);
      setSelectedCompany(null);
      setIsAuthenticated(false);
      clearEmpresaFromUrl({ pathname: '/' });
      navigate('/', { replace: true });
      setIsLoading(false);
    }
  }, [navigate, user?.id]);

  const selectCompany = useCallback((company) => {
    if (!company?.id) return;

    clearForceCompanySelector();
    setForceCompanySelector(false);
    authService.setSelectedCompany(company);
    setSelectedCompany(company);
    navigate(pathWithEmpresa('/', company.id), { replace: true });
  }, [navigate]);

  const clearCompanySelection = useCallback(() => {
    if (usuarioSoloUnaEmpresa(user)) {
      return;
    }
    prepareCompanyChange();
    setForceCompanySelector(true);
    authService.setSelectedCompany(null);
    setSelectedCompany(null);
    navigate('/', { replace: true });
  }, [navigate, user]);

  const updateUser = useCallback(async () => {
    if (!authService.isAuthenticated()) {
      return { success: false, message: 'Usuario no autenticado' };
    }

    try {
      const result = await authService.getCurrentUser();
      if (result.success) {
        setUser(result.user);
      }
      return result;
    } catch (error) {
      console.error('Error al actualizar usuario:', error);
      return { success: false, message: 'Error de conexión' };
    }
  }, []);

  const hasPermission = useCallback((permission) => {
    return Boolean(user?.permissions?.includes(permission));
  }, [user]);

  const hasRole = useCallback((role) => {
    return Boolean(user?.roles?.some((userRole) => userRole.name === role));
  }, [user]);

  const value = useMemo(() => ({
    user,
    selectedCompany,
    empresaIdFromUrl,
    isLoading,
    isResolvingCompany,
    isAuthenticated,
    forceCompanySelector,
    login,
    register,
    logout,
    selectCompany,
    clearCompanySelection,
    updateUser,
    hasPermission,
    hasRole,
    getToken: authService.getToken.bind(authService),
    clearStorage: authService.clearStorage.bind(authService),
  }), [
    user,
    selectedCompany,
    empresaIdFromUrl,
    isLoading,
    isResolvingCompany,
    isAuthenticated,
    forceCompanySelector,
    login,
    register,
    logout,
    selectCompany,
    clearCompanySelection,
    updateUser,
    hasPermission,
    hasRole,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
};
